export type Options = {[key: string]: string | {long: string, short: string}};
export type Variable = {
    title: string,
    options: Options,
    customize?: {
        minimum: number,
        maximum: number,
        step: number,
        choose: {[locale: string]: string},
        prompt: {[locale: string]: string},
        information?: {[locale: string]: string},
    },
};
export type Variables = {[dimension: string]: Variable};
export type Product = {
    title?: {[locale: string]: string},
    ready?: string,
    payload?: string,
    cart?: string,
    material?: {[locale: string]: string},
    additional?: {[locale: string]: {title?: string, text: string}},
    dimensions?: {key: string, title?: string},
    seats?: {[size: string]: number},
    shipping_type?: ShippingType,
    inquiry?: true | string,
    nogo?: boolean,
};
export type ShippingType = 'parcel' | 'forwarder';
export type CountryNames = {[code: string]: string};
export type Availability = Array<number | null>;
export type Readyness = string | {[country: string]: string};
export type ReadyInStock = {[product: string]: Readyness};

const PAYLOAD_SIZE_REGEX = /^\d+(x\d+)*|\d+\*$/;

export type Payload = {
    value: string,
    parts: Array<string>,
    size?: Array<number>,
    dimension?: number,
    sizePart?: number,
};

function parsePayload(payload: string | undefined): Payload | undefined {
    if(!payload) return;
    const parts = payload.split('-');
    const result: Payload = {value: payload, parts: parts};
    for(let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if(part.match(PAYLOAD_SIZE_REGEX)) {
            const size = part.split('x').map(s => parseInt(s)),
                  dimension = size.reduce((a,b) => a*b, 1),
                  sizePart = i;
            Object.assign(result, {
                size, dimension, sizePart,
            });
        }
    }
    return result;
}

export function payloadsOnlyDifferInSize(a: Payload, b: Payload): boolean {
    if(!a.size || !b.size)
        return false;
    if(a.sizePart != b.sizePart)
        return false;
    if(a.parts.length != b.parts.length)
        return false;
    return a.parts.every((v,i) => i == b.sizePart || v === b.parts[i]);
}

export function getBetterAlternatives(
    items: Array<V1.Product>,
    thisItem: V1.Product,
    accept: (item: [item: V1.Product, payload: Payload]) => boolean,
    predicate: (a: [item: V1.Product, payload: Payload], b: [item: V1.Product, payload: Payload]) => boolean,
    precedence: (a: [item: V1.Product, payload: Payload], b: [item: V1.Product, payload: Payload]) => number,
): [payload: Payload, alternatives: Array<[item: V1.Product, payload: Payload]>] | undefined {
    const thisPayload = parsePayload(thisItem.payload);
    if(!thisPayload) return;
    if(!accept([thisItem, thisPayload]))
        return;
    const alternatives = items
        .map(otherItem => {
            if(otherItem === thisItem) return;
            var otherPayload = parsePayload(otherItem.payload);
            if(!otherPayload) return;
            if(predicate([thisItem, thisPayload], [otherItem, otherPayload]))
                return [otherItem, otherPayload];
        })
        .filter(item => item)
        .sort((a, b) =>
            precedence([thisItem, thisPayload], a as any) -
            precedence([thisItem, thisPayload], b as any)
        );
    if(alternatives.length)
        return [thisPayload, alternatives as any];
}

const READY_IN_STOCK_COUNTRIES = ['DE', 'AT', 'CH', 'BE', 'NL', 'LU'];

(globalThis as any).READY_IN_STOCK_COUNTRIES = READY_IN_STOCK_COUNTRIES;

export function itemReady(
    item: V1.Product,
    country: string | null,
    ready: Readyness | undefined,
    items: Array<V1.Product>,
    availability: Availability,
    readyInStock: ReadyInStock,
): {readyIn: Readyness, inStock: number} | undefined {
    const index = items.indexOf(item);
    if(index < 0)
        return;
    if(!country)
        return;
    if(READY_IN_STOCK_COUNTRIES.includes(country)) {
        const inStock = availability[index];
        if(inStock)
            return {readyIn: readyInStock[item.item_no] || readyInStock['*'], inStock};
    }
    if(!item.shipping_costs[country] && !item.shipping_costs['*'])
        return;
    if(!ready)
        return;
    return {readyIn: ready, inStock: 0};
}

export function computeSeats(size: string | undefined, PRODUCT: Product) {
    if(!size)
        return;
    const dimension = String(size).split('x')[0];
    if(!dimension)
        return;
    return PRODUCT.seats?.[dimension];
}
